<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-row gap-4">
      <FontAwesomeIcon :icon="['fal', 'folder']" size="2x" />
      <h2 class="capitalize-first text-2xl font-medium">
        {{ $t('global.dossier_place') }}
      </h2>
    </div>
    <template v-if="loading">
      <LoadingComponent />
    </template>
    <div v-else class="flex-1">
      <div class="grid grid-cols-2 gap-3">
        <template v-for="address in filteredAddresses" :key="address.type">
          <div
            v-if="address.name !== null"
            @click="handelSelectAddress(address)"
          >
            <BaseCard
              title="title"
              :entry="address"
              :selected="
                placeType === address.type &&
                (!address.id || (address.id && placeId === address.id)) &&
                (address.name === placeModel.name || placeType === 'LDEC')
              "
            >
              <template #card-details="{ entry: address }">
                <div class="flex flex-col">
                  <div v-if="address.street">
                    {{ address.street }}
                  </div>
                  <div v-if="address?.city">
                    {{ `${address?.zip} ${address?.city}` }}
                  </div>
                </div>
              </template>
            </BaseCard>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseCard from '@c/BaseCard.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import PlaceType from '@/assets/enums/config/PlaceType'

export default {
  name: 'SelectDossierPlace',
  components: { LoadingComponent, FontAwesomeIcon, BaseCard },
  props: {
    placeModel: {
      type: Object,
      required: true
    },
    placeType: {
      type: String,
      required: true
    },
    placeId: {
      type: [String, Number],
      required: true
    },
    dossierId: {
      type: String,
      required: false,
      default: null
    },
    availablePlaceType: {
      type: Array,
      required: true
    }
  },
  emits: [
    'update:placeType',
    'update:placeId',
    'update:placeName',
    'close',
    'update:placeModel',
    'update:placeStreet',
    'update:placeExtraStreet',
    'update:placeCity',
    'update:placeCityId',
    'update:placeZip',
    'update:placeCountry',
    'update:placeOther'
  ],
  data() {
    return {
      loading: false,
      addresses: []
    }
  },
  computed: {
    ...mapGetters({
      dossier: 'dossier/getDossier',
      auth: 'auth/getAuth'
    }),
    filteredAddresses() {
      return this.addresses.filter((address) =>
        this.availablePlaceType.includes(address.type)
      )
    }
  },
  mounted() {
    if (this.dossier) {
      this.addAddress({
        type: PlaceType.DOMICILE,
        id: null,
        title: this.$t('attributes.DOM'),
        name: this.$t('attributes.DOM'),
        street: this.dossier.concernedPerson.city_object
          ? this.dossier.concernedPerson.city_object.street
          : this.dossier.concernedPerson.street,
        city: this.dossier.concernedPerson.city_object
          ? this.dossier.concernedPerson.city_object.name
          : this.dossier.concernedPerson.city,
        zip: this.dossier.concernedPerson.city_object
          ? this.dossier.concernedPerson.city_object.zip
          : this.dossier.concernedPerson.zip
      })
      this.addAddress({
        type: PlaceType.LIEU_DECES,
        id: null,
        title: this.dossier.concernedPerson.place_of_death
          ? this.$t('attributes.death_place') +
            ' - ' +
            this.dossier.concernedPerson.place_of_death.city
          : this.$t('attributes.death_place'),
        name: this.$t('attributes.death_place'),
        street: this.dossier.concernedPerson.place_of_death
          ? this.dossier.concernedPerson.place_of_death.street
          : this.dossier.concernedPerson.death_street,
        city: this.dossier.concernedPerson.place_of_death
          ? this.dossier.concernedPerson.place_of_death.city
          : this.dossier.concernedPerson.city,
        zip: this.dossier.concernedPerson.place_of_death
          ? this.dossier.concernedPerson.place_of_death.zip
          : this.dossier.concernedPerson.zip
      })
      if (
        this.dossier.events &&
        Array.isArray(this.dossier.events) &&
        this.dossier.events.length
      ) {
        this.dossier.events.forEach((event) => {
          if (
            event.place_kind &&
            event.place_kind !== 'LDEC' &&
            event.place_kind !== 'OTHER' &&
            event.place_kind !== 'RUE'
          ) {
            this.addAddress({
              ...event.place_model,
              type: event.place_kind,
              id: event.place_id,
              title:
                this.$t(`enums.event.type.${event.type}`) +
                  ' - ' +
                  (event?.destination_model?.full_name ||
                    event?.place_model?.name?.[this.$i18n.locale] ||
                    event?.place_model?.name) || event?.place_other,
              city:
                event?.place_model?.name?.[this.$i18n.locale] ??
                event?.place_model?.city,
              zip: event?.place_model?.address?.zip || event?.place_model?.zip,
              street:
                event?.place_model?.address?.street ||
                event?.place_model?.street
            })
          }
          if (['OTHER', 'RUE'].includes(event.place_kind)) {
            if (event.destination_other) {
              this.addAddress({
                ...event.destination_model,
                type: event.destination_kind,
                title:
                  this.$t(`enums.event.type.${event.type}`) +
                  ' - ' +
                  (event?.destination_other || event?.place_other),
                full_name: event?.destination_other,
                name: event?.destination_other,
                street: event?.destination_street,
                extra_street: event?.destination_extra_street,
                city: event?.destination_city,
                zip: event?.destination_zip,
                country: event?.destination_country
              })
            }
            this.addAddress({
              ...event.destination_model,

              type: event.place_kind,
              title:
                this.$t(`enums.event.type.${event.type}`) +
                ' - ' +
                event?.place_other,
              full_name: event?.place_other,
              name: event?.place_other,
              street: event?.place_street,
              extra_street: event?.place_extra_street,
              city: event?.place_city,
              zip: event?.place_zip,
              country: event?.place_country
            })
          }
          if (event.destination_kind) {
            this.addAddress({
              ...event?.destination_model,
              type: event.destination_kind,
              id: event.destination_id,
              title:
                this.$t(`enums.event.type.${event.type}`) +
                ' - ' +
                (event?.destination_model?.name?.[this.$i18n.locale] ||
                  event?.place_other ||
                  event?.destination_model?.name),
              city: event?.destination_model?.name?.[this.$i18n.locale],
              zip: event?.destination_model?.address?.zip || ''
            })
          }
        })
      }
    }
  },
  methods: {
    ...mapActions({
      processFetchAddressesByDossierAction: 'dossier/fetchAddressesByDossier'
    }),
    handelSelectAddress(address) {
      this.$emit('update:placeType', address.type)
      if (this.placeId === address.id) {
        this.$emit('update:placeId', null)
        this.$emit('update:placeModel', address ?? {})
        this.$emit('update:placeStreet', null)
        this.$emit('update:placeExtraStreet', null)
        this.$emit('update:placeCity', null)
        this.$emit('update:placeCityId', null)
        this.$emit('update:placeZip', null)
        this.$emit('update:placeCountry', null)
        this.$emit('update:placeOther', null)
      } else {
        this.$emit('update:placeId', address.id ?? null)
        this.$emit('update:placeModel', address)

        if (address.type === PlaceType.AUTRES) {
          this.$emit('update:placeStreet', address.street)
          this.$emit('update:placeExtraStreet', address.extra_street)
          this.$emit('update:placeCity', address.city)
          this.$emit('update:placeCityId', address.city_id)
          this.$emit('update:placeZip', address.zip)
          this.$emit('update:placeCountry', address.country)
          this.$emit('update:placeOther', address.full_name)
        }
      }

      this.$emit('close', true)
    },
    addAddress(place) {
      if (
        !this.addresses.some(
          (item) =>
            item.id === place.id &&
            item.type === place.type &&
            item.name === place.name
        )
      ) {
        this.addresses.push(place)
      }
    }
  }
}
</script>

<style scoped></style>
